import { FC, useRef, useCallback, useEffect, useState } from 'react';
import {
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Link,
  Undo,
  Redo
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  minHeight?: string;
}

interface ToolbarButtonProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  command: string;
  value?: string;
  isActive?: boolean;
  onClick: (command: string, value?: string) => void;
}

const ToolbarButton: FC<ToolbarButtonProps> = ({ 
  icon: Icon, 
  title, 
  command, 
  value, 
  isActive, 
  onClick 
}) => (
  <button
    type="button"
    title={title}
    onClick={() => onClick(command, value)}
    className={`p-2 rounded transition-colors ${
      isActive 
        ? 'bg-primary/10 text-primary' 
        : 'hover:bg-gray-100 text-muted-foreground'
    }`}
  >
    <Icon className="w-4 h-4" />
  </button>
);

export const RichTextEditor: FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder,
  className = "",
  minHeight = "100px"
}) => {
  const { t } = useLanguage();
  const editorRef = useRef<HTMLDivElement>(null);
  const [editorState, setEditorState] = useState({
    bold: false,
    italic: false,
    underline: false,
  });

  // Initialize editor with value
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  // Update editor state based on cursor position
  const updateEditorState = useCallback(() => {
    setEditorState({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
    });
  }, []);

  // Handle content changes
  const handleContentChange = useCallback(() => {
    if (editorRef.current) {
      const content = editorRef.current.innerHTML;
      onChange(content);
    }
  }, [onChange]);

  // Execute formatting commands
  const executeCommand = useCallback((command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    updateEditorState();
    handleContentChange();
  }, [updateEditorState, handleContentChange]);

  // Handle keyup to update state
  const handleKeyUp = useCallback(() => {
    updateEditorState();
    handleContentChange();
  }, [updateEditorState, handleContentChange]);

  // Handle mouse up to update state
  const handleMouseUp = useCallback(() => {
    updateEditorState();
  }, [updateEditorState]);

  // Prevent default behavior for certain keys
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      executeCommand('insertText', '    '); // Insert 4 spaces for tab
    }
  }, [executeCommand]);

  // Insert link with prompt
  const insertLink = useCallback(() => {
    const url = prompt(t('editor.enter_url'));
    if (url) {
      executeCommand('createLink', url);
    }
  }, [executeCommand, t]);

  // Clean paste content
  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    executeCommand('insertText', text);
  }, [executeCommand]);

  return (
    <div className={`rich-editor border border-input rounded-lg overflow-hidden ${className}`}>
      {/* Toolbar */}
      <div className="flex items-center gap-1 p-2 border-b border-gray-200 bg-background">
        {/* Text formatting */}
        <ToolbarButton
          icon={Bold}
          title={t('editor.bold')}
          command="bold"
          isActive={editorState.bold}
          onClick={executeCommand}
        />
        <ToolbarButton
          icon={Italic}
          title={t('editor.italic')}
          command="italic"
          isActive={editorState.italic}
          onClick={executeCommand}
        />
        <ToolbarButton
          icon={Underline}
          title={t('editor.underline')}
          command="underline"
          isActive={editorState.underline}
          onClick={executeCommand}
        />

        {/* Separator */}
        <div className="w-px h-6 bg-gray-300 mx-1" />

        {/* Lists */}
        <ToolbarButton
          icon={List}
          title={t('editor.bullet_list')}
          command="insertUnorderedList"
          onClick={executeCommand}
        />
        <ToolbarButton
          icon={ListOrdered}
          title={t('editor.numbered_list')}
          command="insertOrderedList"
          onClick={executeCommand}
        />

        {/* Separator */}
        <div className="w-px h-6 bg-gray-300 mx-1" />

        {/* Alignment */}
        <ToolbarButton
          icon={AlignLeft}
          title={t('editor.align_left')}
          command="justifyLeft"
          onClick={executeCommand}
        />
        <ToolbarButton
          icon={AlignCenter}
          title={t('editor.align_center')}
          command="justifyCenter"
          onClick={executeCommand}
        />
        <ToolbarButton
          icon={AlignRight}
          title={t('editor.align_right')}
          command="justifyRight"
          onClick={executeCommand}
        />

        {/* Separator */}
        <div className="w-px h-6 bg-gray-300 mx-1" />

        {/* Link */}
        <ToolbarButton
          icon={Link}
          title={t('editor.insert_link')}
          command="createLink"
          onClick={insertLink}
        />

        {/* Separator */}
        <div className="w-px h-6 bg-gray-300 mx-1" />

        {/* Undo/Redo */}
        <ToolbarButton
          icon={Undo}
          title={t('editor.undo')}
          command="undo"
          onClick={executeCommand}
        />
        <ToolbarButton
          icon={Redo}
          title={t('editor.redo')}
          command="redo"
          onClick={executeCommand}
        />

        {/* Format type selector */}
        <select
          className="ml-auto text-sm border border-input rounded px-2 py-1"
          onChange={(e) => executeCommand('formatBlock', e.target.value)}
          defaultValue="div"
        >
          <option value="div">{t('editor.normal')}</option>
          <option value="h1">{t('editor.heading_1')}</option>
          <option value="h2">{t('editor.heading_2')}</option>
          <option value="h3">{t('editor.heading_3')}</option>
          <option value="p">{t('editor.paragraph')}</option>
        </select>
      </div>

      {/* Editor content */}
      <div
        ref={editorRef}
        contentEditable
        onKeyUp={handleKeyUp}
        onMouseUp={handleMouseUp}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        className="p-3 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-inset prose prose-sm max-w-none"
        style={{
          minHeight,
          '--placeholder-text': `"${placeholder || t('editor.placeholder')}"`
        } as React.CSSProperties}
        data-placeholder={placeholder || t('editor.placeholder')}
        suppressContentEditableWarning
      />

      {/* Embedded CSS for placeholder */}
      <style>
        {`
          .rich-editor [contenteditable]:empty:before {
            content: attr(data-placeholder);
            color: #9ca3af;
            font-style: italic;
            pointer-events: none;
          }
          .rich-editor [contenteditable]:focus:before {
            display: none;
          }
          .rich-editor [contenteditable] {
            outline: none;
          }
        `}
      </style>
    </div>
  );
};